import {useEffect, useState} from 'react';
import appStore from 'client/AppStore';
import {useShallow} from 'zustand/react/shallow';

import {Set} from 'immutable';
import {addToast} from '@albert-io/atomic';

import {getFolderQuestionSetsQuery} from 'client/FolderPracticeView/folderPracticeView.queries';

import getSmartSelectQuery from 'client/components/AssignModal/AssignModal.queries';

import {getQuestionSetsByGuideLevelIdQuery} from 'resources/Question/QuestionSet.queries';

import {getQuestionSetsQuery} from 'client/CreateAssignmentV2/utils/createAssignment.queries';

import {
  getAssignmentById,
  getAssignmentQuestionSets
} from '../../../ViewAssignment/ViewAssignment.queries';
import {ViewLocations} from '../../../store/slices/Navigation.store';
import {useContentDiscoveryStore} from '../../../store';
import {
  QUESTION_SETS_QUERY_PAGE_SIZE,
  fetchAllPaginatedResults
} from 'client/components/ContentDiscovery/ContentDiscovery.utils';

export const fetchQuestionSetsWithIds = async ({
  resourceType,
  resourceId,
  excludedQuestionIds,
  includedQuestionIds,
  numSmartAssignQuestions,
  onProgressUpdate,
  onTotalCountKnown,
  onComplete,
  onError
}: {
  resourceType: string;
  resourceId: string | null;
  excludedQuestionIds?: string[];
  includedQuestionIds?: string[];
  numSmartAssignQuestions?: number;
  onProgressUpdate?: (questions: any) => void;
  onTotalCountKnown?: (count: number) => void;
  onComplete?: () => void;
  onError?: () => void;
}) => {
  try {
    let questions;
    if (numSmartAssignQuestions && numSmartAssignQuestions > 0) {
      const smartQuery = getSmartSelectQuery(numSmartAssignQuestions, resourceId, resourceType);
      const queryResult = await smartQuery.getResourcePromise();

      const questionSetIds = queryResult
        .getMeta()
        .getSmartSelectResults()
        .map((question) => {
          const {id} = question.toJS();
          return id;
        });

      const allIds = Set(questionSetIds).toArray();
      const totalPages = Math.ceil(allIds.length / QUESTION_SETS_QUERY_PAGE_SIZE);

      try {
        questions = await fetchAllPaginatedResults({
          getPageQuery: (page) => {
            const startIdx = (page - 1) * QUESTION_SETS_QUERY_PAGE_SIZE;
            const endIdx = startIdx + QUESTION_SETS_QUERY_PAGE_SIZE;
            const pageIds = allIds.slice(startIdx, endIdx);

            return resourceType === 'folder'
              ? getFolderQuestionSetsQuery(resourceId, QUESTION_SETS_QUERY_PAGE_SIZE, pageIds).page(
                  1
                )
              : getQuestionSetsByGuideLevelIdQuery(
                  resourceId,
                  QUESTION_SETS_QUERY_PAGE_SIZE,
                  pageIds
                ).page(1);
          },
          totalPages,
          onFirstPageFetched: () => {
            if (onTotalCountKnown) {
              onTotalCountKnown(allIds.length);
            }
          },
          onProgressUpdate
        });

        if (onComplete) {
          onComplete();
        }
      } catch (_error) {
        if (onError) {
          onError();
        }
      }

      return questions;
    } else if (excludedQuestionIds && excludedQuestionIds.length > 0) {
      const resourceStr = resourceType === 'folder' ? 'templates_v1' : 'guide_levels_v2';

      try {
        questions = await fetchAllPaginatedResults({
          getPageQuery: (page) =>
            getQuestionSetsQuery(resourceStr, resourceId, excludedQuestionIds)
              .pageSize(QUESTION_SETS_QUERY_PAGE_SIZE)
              .page(page),
          onFirstPageFetched: ({totalEntries}) => {
            if (onTotalCountKnown) {
              onTotalCountKnown(totalEntries);
            }
          },
          onProgressUpdate: (accumulatedQuestions) => {
            if (onProgressUpdate) {
              onProgressUpdate(accumulatedQuestions);
            }
          }
        });

        if (onComplete) {
          onComplete();
        }
      } catch (_error) {
        if (onError) {
          onError();
        }
      }

      return questions;
    } else {
      try {
        questions = await fetchAllPaginatedResults({
          getPageQuery: (page) =>
            resourceType === 'folder'
              ? getFolderQuestionSetsQuery(
                  resourceId,
                  QUESTION_SETS_QUERY_PAGE_SIZE,
                  includedQuestionIds
                ).page(page)
              : getQuestionSetsByGuideLevelIdQuery(
                  resourceId,
                  QUESTION_SETS_QUERY_PAGE_SIZE,
                  includedQuestionIds
                ).page(page),
          onFirstPageFetched: ({totalEntries}) => {
            if (onTotalCountKnown) {
              onTotalCountKnown(totalEntries);
            }
          },
          onProgressUpdate: (accumulatedQuestions) => {
            if (onProgressUpdate) {
              onProgressUpdate(accumulatedQuestions);
            }
          }
        });

        if (onComplete) {
          onComplete();
        }
      } catch (_error) {
        if (onError) {
          onError();
        }
      }

      return questions;
    }
  } catch (_e) {
    return null;
  }
};

export const useHandleAssignmentBuilderInitializationHook = (query, pathname) => {
  const {
    setfolderId,
    fetchAndSetFolder,
    savedQuestionSets,
    currentFolderId,
    setLocation,
    setInitialName,
    setSelectedQuestionSets,
    selectedQuestionSets,
    isDraft,
    numSmartAssignQuestions,
    legacyItemId,
    setLegacyItemId,
    setNumSmartAssignQuestions,
    legacyQuestionsIds,
    setLegacyQuestionsIds,
    legacyQuestionsIdsExcluded,
    setLegacyQuestionsIdsExcluded,
    setLegacyItemType,
    legacyItemType,
    setLegacyQuestionSets,
    setTotalQuestionCount,
    setIsQuestionsQueryLoading
  } = useContentDiscoveryStore(
    useShallow((state) => ({
      setfolderId: state.folderSlice.setFolderId,
      fetchAndSetFolder: state.folderSlice.fetchAndSetFolderOrError,
      savedQuestionSets: state.folderSlice.savedQuestionSets,
      currentFolderId: state.folderSlice.folderId,
      setLocation: state.navigation.setViewLocation,
      setInitialName: state.navigation.setInitialName,
      setSelectedQuestionSets: state.draft.setSelectedQuestionSets,
      selectedQuestionSets: state.draft.selectedQuestionSets,
      isDraft: state.draft.isDraft,
      numSmartAssignQuestions: state.draft.numSmartAssignQuestions,
      legacyItemId: state.draft.legacyItemId,
      setLegacyItemId: state.draft.setLegacyItemId,
      setNumSmartAssignQuestions: state.draft.setNumSmartAssignQuestions,
      legacyQuestionsIds: state.draft.legacyQuestionsIds,
      setLegacyQuestionsIds: state.draft.setLegacyQuestionsIds,
      legacyQuestionsIdsExcluded: state.draft.legacyQuestionsIdsExcluded,
      setLegacyQuestionsIdsExcluded: state.draft.setLegacyQuestionsIdsExcluded,
      legacyItemType: state.draft.legacyItemType,
      setLegacyItemType: state.draft.setLegacyItemType,
      legacyQuestionSets: state.draft.legacyQuestionSets,
      setLegacyQuestionSets: state.draft.setLegacyQuestionSets,
      setTotalQuestionCount: state.draft.setTotalQuestionCount,
      setIsQuestionsQueryLoading: state.draft.setIsQuestionsQueryLoading
    }))
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchQuestionSetsOnMount = async (type) => {
      setIsLoading(true);
      setIsQuestionsQueryLoading(true);
      try {
        let result;
        if (numSmartAssignQuestions > 0) {
          result = await fetchQuestionSetsWithIds({
            resourceType: type,
            resourceId: legacyItemId,
            numSmartAssignQuestions,
            onProgressUpdate: (updatedQuestions) => {
              setSelectedQuestionSets(updatedQuestions);
              setLegacyQuestionSets(updatedQuestions);
            },
            onTotalCountKnown: (count) => {
              setTotalQuestionCount(count);
              setIsLoading(false);
            },
            onComplete: () => {
              setIsQuestionsQueryLoading(false);
            }
          });
        } else if (legacyQuestionsIdsExcluded && legacyQuestionsIdsExcluded.length > 0) {
          result = await fetchQuestionSetsWithIds({
            resourceType: type,
            resourceId: legacyItemId,
            excludedQuestionIds: legacyQuestionsIdsExcluded,
            onProgressUpdate: (updatedQuestions) => {
              setSelectedQuestionSets(updatedQuestions);
              setLegacyQuestionSets(updatedQuestions);
            },
            onTotalCountKnown: (count) => {
              setTotalQuestionCount(count);
              setIsLoading(false);
            },
            onComplete: () => {
              setIsQuestionsQueryLoading(false);
            }
          });
        } else {
          result = await fetchQuestionSetsWithIds({
            resourceType: type,
            resourceId: legacyItemId,
            includedQuestionIds: legacyQuestionsIds,
            onProgressUpdate: (updatedQuestions) => {
              setSelectedQuestionSets(updatedQuestions);
              setLegacyQuestionSets(updatedQuestions);
            },
            onTotalCountKnown: (count) => {
              setTotalQuestionCount(count);
              setIsLoading(false);
            },
            onComplete: () => {
              setIsQuestionsQueryLoading(false);
            }
          });
        }

        const questions = result;

        setSelectedQuestionSets(questions);
        setLegacyQuestionSets(questions);
      } catch (_e) {
        addToast({
          message: 'Something went wrong with fetching your questions.',
          title: 'Error fetching questions',
          color: 'negative'
        });
        setIsLoading(false);
        setIsQuestionsQueryLoading(false);
      }
      setLegacyItemId(null);
      setLegacyQuestionsIds([]);
      setLegacyQuestionsIdsExcluded([]);
      setLegacyItemType(null);
    };

    // Handles fetching folder or assignment data and calling respective setters
    const fetchData = async (id: string, type: 'folder' | 'assignment') => {
      setIsLoading(true);
      try {
        if (type === 'folder') {
          setfolderId(id);

          // If we already have savedQuestionSets for this folder, use them
          if (currentFolderId === id && savedQuestionSets.size > 0) {
            setSelectedQuestionSets(savedQuestionSets);
            setIsLoading(false);
          } else if (selectedQuestionSets.size === 0) {
            const {questionSets, errorLoadingFolder, error} = await fetchAndSetFolder(id);
            if (errorLoadingFolder) {
              throw error;
            }
            setSelectedQuestionSets(questionSets);
            // The folder store will continue loading remaining pages in background
            setIsLoading(false);
          } else {
            setIsLoading(false);
          }
        } else if (type === 'assignment') {
          const [fetchedAssignment, fetchedAssignmentQuestionSets] = await Promise.all([
            getAssignmentById(id, isDraft),
            getAssignmentQuestionSets(id, isDraft)
          ]);
          setSelectedQuestionSets(fetchedAssignmentQuestionSets);
          setInitialName(fetchedAssignment.getName());
          setIsLoading(false);
        }
      } catch (_err) {
        // TODO: error handling for failed prefilling attempts
        setIsLoading(false);
      }
    };

    const location = getViewLocationFromPath(pathname);
    setLocation(location);
    // prefill logic from query params
    let {folderId: prefillFolderId} = query; // default to query param for folder id
    const {assignmentId: queryAssignmentId} = query;

    if (legacyItemId) {
      fetchQuestionSetsOnMount(legacyItemType);
    } else if (location === 'IN_ASSIGNMENT_BUILDER') {
      if (prefillFolderId?.length > 0) {
        fetchData(prefillFolderId, 'folder');
      }
      // TODO: preload from assignment id in query
      else if (queryAssignmentId?.length) {
        fetchData(queryAssignmentId, 'assignment');
      }
    } else if (location === 'IN_FOLDER_VIEW') {
      prefillFolderId = appStore.routerProps.params.folderId; // use param instead if in folder view
      fetchData(prefillFolderId, 'folder');
    }
    // TODO: consider moving assignment view prefil logic here
  }, [
    query,
    pathname,
    setLocation,
    fetchAndSetFolder,
    setInitialName,
    setSelectedQuestionSets,
    setfolderId,
    isDraft,
    legacyItemId,
    numSmartAssignQuestions,
    setLegacyItemId,
    setNumSmartAssignQuestions,
    setLegacyQuestionsIds,
    setLegacyQuestionsIdsExcluded,
    legacyQuestionsIds,
    legacyQuestionsIdsExcluded,
    legacyItemType,
    setLegacyItemType
  ]);

  // set selected question sets from fetched folder (if fetched)

  return {
    isLoading
  };
};

const getViewLocationFromPath = (pathname: string) => {
  let location: ViewLocations = 'IN_ASSIGNMENT_BUILDER';
  if (pathname.includes('folder')) {
    location = 'IN_FOLDER_VIEW';
  }
  return location;
};

export default useHandleAssignmentBuilderInitializationHook;
